import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ImportSongWithoutFiles, UploadStatus } from 'app/components/Import';
import {
  CsvImportProgressPayload,
  WsImportResourceStatus,
} from 'types/websocket.types';
import {
  getCurrentImportEffect,
  importCSVEffect,
  importLyricsEffect,
} from './effects';
import {
  getimportStatus,
  importInitialState,
  isImportDone,
} from './slice.utils';

const slice = createSlice({
  name: 'import',
  initialState: importInitialState,
  reducers: {
    clearCsv(state) {
      state.csv = undefined;
    },
    clearFileUpload(state) {
      state.songs = [];
      delete state.status;
    },
    setImportSongsAction(
      state,
      { payload }: PayloadAction<ImportSongWithoutFiles[]>
    ) {
      state.songs = payload;
      state.status = getimportStatus(payload);
    },
    setAudioUploadedAction(
      state,
      {
        payload: { audioTitle, status },
      }: PayloadAction<{ audioTitle: string; status: UploadStatus }>
    ) {
      const { songs } = state;

      const updatedSongs = songs.map((song) => ({
        ...song,
        audios: song.audios.map((audio) => {
          if (audio.audioTitle !== audioTitle) {
            return audio;
          }
          return {
            ...audio,
            status,
          };
        }),
      }));

      const inconsistent = updatedSongs.some(({ guid }) => !guid);
      const failed = updatedSongs.some(({ audios }) =>
        audios.some((a) => a.status === 'failed')
      );
      state.status = updatedSongs.some(({ audios }) =>
        audios.some((a) => a.status === 'loading')
      )
        ? 'in_progress'
        : inconsistent
        ? 'inconsistent'
        : failed
        ? 'failed'
        : 'success';

      state.songs = updatedSongs;
    },
    updateImportProgressAction(
      state,
      { payload }: PayloadAction<CsvImportProgressPayload>
    ) {
      if (state.csv && state.guid === payload.guid) {
        state.csv.rowsProcessed = Math.max(
          payload.rowsProcessed,
          state.csv.rowsProcessed || 0
        );
        state.csv.rowCount = payload.rowCount;

        state.csv.status =
          payload.payloadType === 'CsvImportProgress' &&
          payload.status === 'FAILED'
            ? 'FAILED_ONE'
            : (payload.status as WsImportResourceStatus);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(importCSVEffect.pending, (state, { meta }) => {
        state.csv = {
          path: meta.arg.csvTitle,
          status: 'PENDING',
        };
      })
      .addCase(importCSVEffect.fulfilled, (state, { payload, meta }) => {
        if (payload) {
          state.guid = payload.guid;
          state.csv = {
            path: meta.arg.csvTitle,
            status: payload.error
              ? 'FAILED'
              : (payload.status as WsImportResourceStatus),
            error: payload.error,
            rowCount: payload.rowCount,
            rowsProcessed: payload.rowsProcessed,
          };
        }
      })
      .addCase(importLyricsEffect.fulfilled, (state, { meta }) => {
        state.songs = state.songs.map((song) => {
          if (song.guid === meta.arg.songGuid) {
            return {
              ...song,
              lyrics: {
                ...song.lyrics,
                status: 'uploaded',
              },
            };
          }
          return song;
        });

        if (isImportDone(state.songs)) {
          state.status = 'success';
        }
      })
      .addCase(importLyricsEffect.rejected, (state, { meta }) => {
        state.songs = state.songs.map((song) => {
          if (song.guid === meta.arg.songGuid) {
            return {
              ...song,
              lyrics: {
                ...song.lyrics,
                status: 'uploaded',
              },
            };
          }
          return song;
        });
      })
      .addCase(getCurrentImportEffect.fulfilled, (state, { payload }) => {
        if (payload) {
          state.guid = payload.guid;
          state.csv = {
            status: payload.status,
            rowCount: payload.rowCount,
            rowsProcessed: payload.rowsProcessed,
          };
        }
      })
      .addCase(getCurrentImportEffect.rejected, (state) => {
        state.csv = {
          status: 'FAILED',
        };
      });
  },
});

export const {
  clearCsv,
  clearFileUpload,
  setImportSongsAction,
  setAudioUploadedAction,
  updateImportProgressAction,
} = slice.actions;

export const importReducer = slice.reducer;
